import { EmailProviderSetupHint } from '@/components/FormShare/EmailProviderSetupHint'
import { EmailRecipientPicker } from '@/components/FormShare/EmailRecipientPicker'
import { EmailTemplateEditorDialog } from '@/components/FormShare/EmailTemplateEditorDialog'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { ScrollArea } from '@/components/ui/scroll-area'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import type {
  EmailSharePreviewContract,
  EmailShareProvider,
  EmailShareTemplateContract,
} from '@/contracts/share'
import {
  buildEmailSharePreview,
  deleteEmailShareTemplate,
  listEmailShareRecipients,
  listEmailShareTemplates,
  saveEmailShareTemplate,
} from '@/lib/data-client/share'
import type { Form } from '@/types/form'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Copy, CopyPlus, MailCheck, PenLine, Plus, Send, Sparkles, Trash2 } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'

type EmailSharePanelProps = {
  form: Form
  publicUrl: string
  onCopy: (value: string, key: string) => void
  copiedKey: string
}

const categoryLabels: Record<EmailShareTemplateContract['category'], string> = {
  general_invitation: '一般問卷邀請',
  b2b_customer_onboarding: 'B2B 新客建檔邀請',
  event_feedback: '活動回饋問卷',
  sales_followup: '業務跟進問卷',
}

export function EmailSharePanel({ form, publicUrl, onCopy, copiedKey }: EmailSharePanelProps) {
  const queryClient = useQueryClient()
  const [templateId, setTemplateId] = useState('')
  const [toEmails, setToEmails] = useState<string[]>([])
  const [ccEmails, setCcEmails] = useState<string[]>([])
  const [subject, setSubject] = useState('')
  const [body, setBody] = useState('')
  const [provider, setProvider] = useState<EmailShareProvider>('webhook')
  const [activeEmailSection, setActiveEmailSection] = useState('recipients')
  const [editingTemplate, setEditingTemplate] = useState<EmailShareTemplateContract | null>(null)
  const [pendingDeleteTemplate, setPendingDeleteTemplate] =
    useState<EmailShareTemplateContract | null>(null)
  const [preview, setPreview] = useState<EmailSharePreviewContract | null>(null)

  const { data: templateResult } = useQuery({
    queryKey: ['mock-email-share-templates', form.id],
    queryFn: () => listEmailShareTemplates(form.id),
  })
  const { data: recipientResult } = useQuery({
    queryKey: ['mock-email-share-recipients'],
    queryFn: listEmailShareRecipients,
  })

  const templates = templateResult?.data ?? []
  const salesRecipients = recipientResult?.data ?? []
  const providerDisabledReason =
    'Provider 未設定，目前只顯示 mock-safe 寄送 contract，不會寄出真實 Email。'
  const selectedTemplate = useMemo(
    () => templates.find((template) => template.id === templateId) ?? templates[0],
    [templateId, templates],
  )

  useEffect(() => {
    if (!selectedTemplate || templateId) return
    setTemplateId(selectedTemplate.id)
  }, [selectedTemplate, templateId])

  useEffect(() => {
    if (!selectedTemplate) return
    setSubject(selectedTemplate.subject)
    setBody(selectedTemplate.body)
    setCcEmails(resolveTemplateDefaultCc(selectedTemplate, salesRecipients))
  }, [selectedTemplate?.id, salesRecipients, selectedTemplate])

  useEffect(() => {
    if (!selectedTemplate || !subject.trim() || !body.trim()) return
    let active = true
    void buildEmailSharePreview({
      formId: form.id,
      templateId: selectedTemplate.id,
      to: toEmails,
      cc: ccEmails,
      subject,
      body,
      trackingUrl: publicUrl,
      provider,
    }).then((result) => {
      if (active) setPreview(result)
    })
    return () => {
      active = false
    }
  }, [body, ccEmails, form.id, provider, publicUrl, selectedTemplate, subject, toEmails])

  async function handleTemplateSave(template: EmailShareTemplateContract) {
    await saveEmailShareTemplate(form.id, template)
    await queryClient.invalidateQueries({ queryKey: ['mock-email-share-templates', form.id] })
    setTemplateId(template.id)
    setSubject(template.subject)
    setBody(template.body)
  }

  async function handleTemplateDelete(template: EmailShareTemplateContract) {
    if (templates.length <= 1) return
    await deleteEmailShareTemplate(form.id, template.id)
    await queryClient.invalidateQueries({ queryKey: ['mock-email-share-templates', form.id] })
    const nextTemplate = templates.find((item) => item.id !== template.id)
    setTemplateId(nextTemplate?.id ?? '')
    setPendingDeleteTemplate(null)
  }

  function createTemplateDraft(source?: EmailShareTemplateContract): EmailShareTemplateContract {
    const now = new Date().toISOString()
    const base = source ?? selectedTemplate
    return {
      id: `${form.id}-email-template-${crypto.randomUUID()}`,
      formId: form.id,
      name: source ? `${source.name} 複本` : '新的問卷寄送範本',
      category: source?.category ?? 'general_invitation',
      description: source?.description ?? '自訂問卷寄送內容與預設 CC 策略。',
      subject: source?.subject ?? '邀請您填寫 {{form.title}}',
      body:
        source?.body ??
        '您好，\n\n請協助填寫「{{form.title}}」。\n問卷連結：{{tracking.url}}\n\n謝謝您。\n{{workspace.name}}',
      defaultCcStrategy: source?.defaultCcStrategy ?? 'none',
      defaultCcSalesUserIds: source?.defaultCcSalesUserIds ?? [],
      variables: base?.variables ?? [
        '{{form.title}}',
        '{{form.url}}',
        '{{tracking.url}}',
        '{{workspace.name}}',
        '{{sender.name}}',
        '{{sender.email}}',
        '{{sales.name}}',
        '{{sales.email}}',
        '{{campaign.code}}',
        '{{recipient.email}}',
        '{{deadline}}',
      ],
      updatedAt: now,
      sourceMeta: base?.sourceMeta ?? {
        source: 'synthetic',
        dataDate: '2026-05-22',
        permissionScope: 'mock-local',
        confidence: 1,
      },
    }
  }

  function jumpToSection(sectionId: string) {
    setActiveEmailSection(sectionId)
    document
      .getElementById(`email-share-section-${sectionId}`)
      ?.scrollIntoView({ block: 'start', behavior: 'smooth' })
  }

  return (
    <div className="grid gap-5 xl:grid-cols-[220px_minmax(0,1fr)]">
      <aside className="rounded-lg border border-slate-200 bg-white p-3 shadow-sm xl:sticky xl:top-0 xl:self-start">
        <div className="mb-3 px-2 text-sm font-semibold text-slate-950">寄送設定</div>
        <nav className="grid gap-1" aria-label="寄送問卷連結操作區塊">
          {[
            { id: 'recipients', label: '收件與 CC' },
            { id: 'template', label: '信件範本' },
            { id: 'content', label: '信件內容' },
            { id: 'preview', label: '預覽與測試' },
            { id: 'provider', label: '寄信設定' },
          ].map((section) => (
            <button
              key={section.id}
              type="button"
              onClick={() => jumpToSection(section.id)}
              className={[
                'rounded-md px-3 py-2 text-left text-sm font-medium transition-colors',
                activeEmailSection === section.id
                  ? 'bg-indigo-50 text-indigo-700'
                  : 'text-slate-600 hover:bg-slate-50 hover:text-slate-950',
              ].join(' ')}
            >
              {section.label}
            </button>
          ))}
        </nav>
      </aside>

      <div className="min-w-0 space-y-5">
        <div className="grid gap-5 2xl:grid-cols-[minmax(0,1fr)_420px]">
        <div className="space-y-5">
          <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
            <div className="flex flex-col gap-3 xl:flex-row xl:items-start xl:justify-between">
              <div>
                <div className="flex items-center gap-2">
                  <MailCheck className="size-5 text-indigo-600" />
                  <h3 className="font-semibold text-slate-950">寄送問卷連結</h3>
                </div>
                <p className="mt-2 text-sm leading-6 text-slate-500">
                  將公開問卷連結寄給指定對象，並可 CC 多位業務。這裡只建立寄送 contract
                  與信件預覽，不會寄出真實 Email。
                </p>
              </div>
              <div className="flex flex-wrap gap-2">
                <Badge>Mock-safe</Badge>
                <Badge variant="warning">Provider not configured</Badge>
              </div>
            </div>
          </div>

          <div
            id="email-share-section-recipients"
            className="grid gap-4 rounded-lg border border-slate-200 bg-white p-4 shadow-sm xl:grid-cols-[minmax(0,1fr)_auto]"
          >
            <div className="space-y-2">
              <Label htmlFor="email-share-public-url">問卷連結</Label>
              <Input
                id="email-share-public-url"
                readOnly
                value={publicUrl}
                className="font-mono text-sm"
              />
            </div>
            <div className="flex items-end">
              <Button
                type="button"
                variant="outline"
                onClick={() => onCopy(publicUrl, 'email-url')}
              >
                <Copy className="size-4" />
                {copiedKey === 'email-url' ? '已複製！' : '複製連結'}
              </Button>
            </div>
          </div>

          <div>
            <EmailRecipientPicker
              toEmails={toEmails}
              ccEmails={ccEmails}
              salesRecipients={salesRecipients}
              onToEmailsChange={setToEmails}
              onCcEmailsChange={setCcEmails}
            />
          </div>

          <div
            id="email-share-section-template"
            className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm"
          >
            <div className="flex flex-col gap-3 xl:flex-row xl:items-start xl:justify-between">
              <div>
                <Label>問卷寄送範本</Label>
                <p className="mt-1 text-sm text-slate-500">
                  選擇範本後可直接調整標題和內容；編輯範本會寫入 persistent mock snapshot。
                </p>
              </div>
              <Button
                type="button"
                variant="outline"
                onClick={() => setEditingTemplate(createTemplateDraft())}
              >
                <Plus className="size-4" />
                新增範本
              </Button>
              <Button
                type="button"
                variant="outline"
                disabled={!selectedTemplate}
                title={!selectedTemplate ? '請先選擇信件範本' : '複製範本'}
                onClick={() => setEditingTemplate(createTemplateDraft(selectedTemplate))}
              >
                <CopyPlus className="size-4" />
                複製範本
              </Button>
              <Button
                type="button"
                variant="outline"
                disabled={!selectedTemplate}
                title={!selectedTemplate ? '請先選擇信件範本' : '編輯範本'}
                onClick={() => setEditingTemplate(selectedTemplate ?? null)}
              >
                <PenLine className="size-4" />
                編輯範本
              </Button>
              <Button
                type="button"
                variant="outline"
                disabled={!selectedTemplate || templates.length <= 1}
                title={
                  !selectedTemplate
                    ? '請先選擇信件範本'
                    : templates.length <= 1
                      ? '至少需要保留一個範本'
                      : '刪除範本'
                }
                onClick={() => setPendingDeleteTemplate(selectedTemplate ?? null)}
              >
                <Trash2 className="size-4" />
                刪除範本
              </Button>
            </div>
            <div className="mt-4 grid gap-4 xl:grid-cols-[280px_minmax(0,1fr)]">
              <Select value={selectedTemplate?.id ?? ''} onValueChange={setTemplateId}>
                <SelectTrigger aria-label="選擇問卷寄送範本">
                  <SelectValue placeholder="選擇信件範本" />
                </SelectTrigger>
                <SelectContent>
                  {templates.map((template) => (
                    <SelectItem key={template.id} value={template.id}>
                      {template.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {selectedTemplate ? (
                <div className="rounded-md bg-slate-50 px-3 py-2 text-sm text-slate-600">
                  <span className="font-medium text-slate-900">
                    {categoryLabels[selectedTemplate.category]}
                  </span>
                  <span className="mx-2 text-slate-300">/</span>
                  {selectedTemplate.description}
                </div>
              ) : null}
            </div>
          </div>

          <div
            id="email-share-section-content"
            className="space-y-4 rounded-lg border border-slate-200 bg-white p-4 shadow-sm"
          >
            <div className="space-y-2">
              <Label htmlFor="email-share-subject">信件標題</Label>
              <Input
                id="email-share-subject"
                value={subject}
                onChange={(event) => setSubject(event.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email-share-body">信件內容</Label>
              <Textarea
                id="email-share-body"
                value={body}
                rows={9}
                onChange={(event) => setBody(event.target.value)}
              />
            </div>
            <div className="rounded-md bg-slate-50 p-3">
              <div className="flex items-center gap-2 text-sm font-medium text-slate-700">
                <Sparkles className="size-4 text-indigo-600" />
                可用變數
              </div>
              <div className="mt-2 flex flex-wrap gap-2">
                {(selectedTemplate?.variables ?? []).map((variable) => (
                  <Badge key={variable} variant="outline" className="bg-white font-mono">
                    {variable}
                  </Badge>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="space-y-5">
          <div
            id="email-share-section-preview"
            className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm"
          >
            <div className="flex items-center justify-between gap-3">
              <div>
                <h3 className="font-semibold text-slate-950">解析後預覽</h3>
                <p className="mt-1 text-sm text-slate-500">依範本變數產生實際信件內容。</p>
              </div>
              <Badge variant="outline">preview-only</Badge>
            </div>
            <div className="mt-4 rounded-md border border-slate-200 bg-slate-50 p-3">
              <div className="text-xs font-medium text-slate-500">Subject</div>
              <div className="mt-1 text-sm font-semibold text-slate-950">
                {preview?.resolvedSubject ?? subject}
              </div>
            </div>
            <ScrollArea
              className="mt-3 h-72 rounded-md border border-slate-200 bg-white"
              viewportClassName="p-3"
            >
              <pre className="whitespace-pre-wrap text-sm leading-6 text-slate-700">
                {preview?.resolvedBody ?? body}
              </pre>
            </ScrollArea>
            <div className="mt-3 rounded-md bg-indigo-50 p-3 text-sm text-indigo-950">
              <div className="font-medium">問卷連結</div>
              <div className="mt-1 break-all font-mono text-xs">
                {preview?.resolvedUrl ?? publicUrl}
              </div>
            </div>
            <div
              id="email-provider-disabled-reason"
              className="mt-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900"
            >
              {providerDisabledReason}
            </div>
            <Button
              type="button"
              className="mt-4 w-full"
              disabled
              title={providerDisabledReason}
              aria-describedby="email-provider-disabled-reason"
            >
              <Send className="size-4" />
              Provider 未設定，測試寄送停用
            </Button>
          </div>

          {preview ? (
            <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
              <h3 className="font-semibold text-slate-950">寄送 contract preview</h3>
              <div className="mt-3 grid gap-2 text-sm">
                <PreviewRow label="Provider" value={preview.provider} />
                <PreviewRow label="Provider mode" value={preview.providerMode} />
                <PreviewRow label="Provider state" value={preview.providerState} />
                <PreviewRow label="收件人" value={`${preview.to.length} 位`} />
                <PreviewRow label="CC" value={`${preview.cc.length} 位`} />
              </div>
              <div className="mt-4 space-y-2">
                {preview.warnings.map((warning) => (
                  <div
                    key={warning}
                    className="rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-900"
                  >
                    {warning}
                  </div>
                ))}
              </div>
            </div>
          ) : null}
        </div>
      </div>

        <div id="email-share-section-provider">
          <EmailProviderSetupHint
            provider={provider}
            providerSetups={preview?.providerSetups ?? []}
            onProviderChange={setProvider}
          />
        </div>

      <EmailTemplateEditorDialog
        open={Boolean(editingTemplate)}
        template={editingTemplate}
        salesRecipients={salesRecipients}
        onOpenChange={(open) => {
          if (!open) setEditingTemplate(null)
        }}
        onSave={(template) => void handleTemplateSave(template)}
      />

      <AlertDialog
        open={Boolean(pendingDeleteTemplate)}
        onOpenChange={(open) => {
          if (!open) setPendingDeleteTemplate(null)
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>刪除問卷寄送範本？</AlertDialogTitle>
            <AlertDialogDescription>
              這只會從 persistent mock snapshot 移除「{pendingDeleteTemplate?.name}
              」範本，不會呼叫後端或寄送服務。
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>取消</AlertDialogCancel>
            <AlertDialogAction
              className="bg-rose-600 text-white hover:bg-rose-700"
              onClick={() => {
                if (pendingDeleteTemplate) void handleTemplateDelete(pendingDeleteTemplate)
              }}
            >
              刪除範本
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      </div>
    </div>
  )
}

function resolveTemplateDefaultCc(
  template: EmailShareTemplateContract,
  salesRecipients: Array<{ email: string; salesUserId?: string }>,
) {
  if (template.defaultCcStrategy === 'none') return []
  const selectedIds = template.defaultCcSalesUserIds
  if (template.defaultCcStrategy === 'form_sales_owner' && selectedIds.length === 0) {
    return salesRecipients.slice(0, 1).map((recipient) => recipient.email)
  }
  return salesRecipients
    .filter((recipient) => selectedIds.includes(recipient.salesUserId ?? ''))
    .map((recipient) => recipient.email)
}

function PreviewRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between gap-3 rounded-md bg-slate-50 px-3 py-2">
      <span className="text-slate-500">{label}</span>
      <span className="font-mono text-xs text-slate-800">{value}</span>
    </div>
  )
}
